class StickyAddToCart extends HTMLElement {
  constructor() {
    super();

    this.stickyProductImage = this.querySelector('.sticky-product-image');
    this.stickyAddToCartButton = this.querySelector('.sticky-add-to-cart-btn');
    this.stickyAddToCartButtonText = this.stickyAddToCartButton.querySelector('.btn-text');
    this.stickyPrice = this.querySelector('.sticky-price');
    this.stickyPriceWas = this.querySelector('.sticky-price-was');
    this.mainProductButtons = document.querySelector('.product-form__buttons');
    this.mainProductImage = document.querySelector('.gallery:not(.gallery--modal) .gallery-thumb.active');
    this.mainProductAddToCart = document.querySelector('.product-form__submit');

    this.addEventListeners();
    this.update();
    this.prefetchPanel();
    this.reserveSpace();
  }

  // The bar is fixed over the page, so at the very bottom it covered the end
  // of the footer (ABN, policies, payment icons). Reserve its height below the
  // page; the bar is always showing there, as the shopper has scrolled past the
  // main add to cart. Re-measured when its height changes (fonts, safe area).
  reserveSpace() {
    // Desktop floats the bar above the bottom edge, so count that gap too.
    const set = () => {
      const gap = parseFloat(getComputedStyle(this).bottom) || 0;
      document.body.style.setProperty('--sticky-atc-height', this.offsetHeight + gap + 'px');
    };
    document.body.classList.add('has-sticky-atc');
    set();
    if (typeof ResizeObserver !== 'undefined') new ResizeObserver(set).observe(this);
    window.addEventListener('resize', set);
  }

  // Fetch the quick add panel as soon as the shopper reaches for the button,
  // as the product cards do, so it is usually ready by the time the tap lands.
  prefetchPanel() {
    const panel = this.querySelector('quick-add-panel');
    if (!panel) return;
    const load = () => {
      if (typeof panel.loadDialog === 'function') panel.loadDialog().catch(() => {});
    };
    ['pointerenter', 'touchstart', 'focus'].forEach((type) =>
      this.stickyAddToCartButton.addEventListener(type, load, { once: true, passive: true })
    );
  }

  addEventListeners() {
    subscribe(PUB_SUB_EVENTS.variantChange, this.update.bind(this));
    this.setupVisibilityObserver();
  }

  // Toggle the sticky bar based on the main add-to-cart button's position:
  //   - main add-to-cart in view          -> hide (mobile + desktop)
  //   - scrolled PAST the add-to-cart      -> show (mobile + desktop)
  //   - add-to-cart still below the fold   -> mobile: show, desktop: hide
  setupVisibilityObserver() {
    const target = this.mainProductButtons || this.mainProductAddToCart;

    // Fallback for missing target / very old browsers: keep it visible.
    if (!target || typeof IntersectionObserver === 'undefined') {
      this.classList.add('visible');
      return;
    }

    this.desktopQuery = window.matchMedia('(min-width: 769px)');
    this.lastEntry = null;

    this.applyVisibility = () => {
      const entry = this.lastEntry;
      if (!entry) return;

      if (entry.isIntersecting) {
        // The real add-to-cart button is on screen.
        this.classList.remove('visible');
      } else if (entry.boundingClientRect.top < 0) {
        // Scrolled past the add-to-cart button (it's above the viewport).
        this.classList.add('visible');
      } else if (this.desktopQuery.matches) {
        // Desktop: not yet reached the button -> stay hidden until scrolled past.
        this.classList.remove('visible');
      } else {
        // Mobile: not yet reached the button -> show.
        this.classList.add('visible');
      }
    };

    this.visibilityObserver = new IntersectionObserver((entries) => {
      this.lastEntry = entries[entries.length - 1];
      this.applyVisibility();
    });
    this.visibilityObserver.observe(target);

    // Re-evaluate when crossing the mobile/desktop breakpoint.
    if (this.desktopQuery.addEventListener) {
      this.desktopQuery.addEventListener('change', this.applyVisibility);
    } else if (this.desktopQuery.addListener) {
      this.desktopQuery.addListener(this.applyVisibility);
    }
  }

  update() {
    this.mainProductImage = document.querySelector('.gallery:not(.gallery--modal) .gallery-thumb.active img');
    if (this.mainProductImage && this.stickyProductImage) this.stickyProductImage.srcset = this.mainProductImage.srcset;

    const price = this.mainPrice()?.textContent.trim();
    if (price) this.stickyPrice.textContent = price;
    // On sale: the page's struck-out original price, shown before the sale price.
    const was = this.mainComparePrice();
    this.stickyPriceWas.hidden = !was;
    if (was) this.stickyPriceWas.textContent = was.textContent.trim();
    this.stickyPrice.parentElement.classList.toggle('sticky-atc-price--sale', !!was);

    const newButtonText = this.mainProductAddToCart.querySelector('span').firstChild.textContent.trim();
    this.stickyAddToCartButtonText.textContent = newButtonText;

    if (newButtonText !== 'ADD TO CART' || this.mainProductAddToCart.disabled) {
      this._waitForKlaviyo().then((klaviyoBtn) => {
        this.stickyAddToCartButtonText.textContent = klaviyoBtn.textContent.trim();
        this.stickyAddToCartButton.onclick = () => klaviyoBtn.click();
      });
    } else {
      this.stickyAddToCartButton.onclick = () => {
        // Products with options open the quick add panel (theme.liquid prints it
        // in the bar); otherwise, or before quick-add.js has loaded, add the
        // variant chosen on the page.
        const panel = this.querySelector('quick-add-panel');
        if (panel && customElements.get('quick-add-panel')) panel.querySelector('[data-qap-open]').click();
        else this.mainProductAddToCart.click();
      };
    }
  }

  // Read from this product's own price block, and re-read every time: the page
  // has other product cards (related products, add-ons) whose sale prices came
  // first in the document, and variant changes re-render the price markup.
  mainPrice() {
    const info = this.mainProductAddToCart.closest('product-info') || document;
    const box = info.querySelector('[id^="price-"][role="status"]') || info;
    return box.querySelector('.price--on-sale .price-item--sale') ||
      box.querySelector('.price__container .price-item--regular') ||
      box.querySelector('.price .price-item');
  }

  mainComparePrice() {
    const info = this.mainProductAddToCart.closest('product-info') || document;
    const box = info.querySelector('[id^="price-"][role="status"]') || info;
    return box.querySelector('.price--on-sale .price__sale s.price-item--regular');
  }

  _waitForKlaviyo() {
    return new Promise((resolve) => {
      const container = document.querySelector('#klaviyo-bis-button-container');
      const btn = container?.querySelector('button');
      if (btn) return resolve(btn);

      const observer = new MutationObserver(() => {
        const container = document.querySelector('#klaviyo-bis-button-container');
        const btn = container?.querySelector('button');

        if (btn) {
          observer.disconnect();
          resolve(btn);
        }
      });

      observer.observe(document.body, { childList: true, subtree: true });
    });
  }
}
customElements.define('sticky-add-to-cart', StickyAddToCart);
